import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  Package,
  Image as ImageIcon,
  Trash2,
  GripVertical,
  X,
  AlertCircle,
  Check,
  Brain,
  RefreshCw,
  Clock,
  Sparkles,
} from 'lucide-react';
import {
  getAdminProduct,
  updateProduct,
  updateProductSection,
  reorderProductMedia,
  deleteProductMedia,
  embedProduct,
} from '../../services/adminService';
import { logger } from '../../lib/logger';

interface ProductMedia {
  id: string;
  url: string;
  type: string;
  alt: string | null;
  sort_order: number;
}

interface ProductSection {
  id: string;
  section_type: string;
  title: string;
  content: string;
  sort_order: number;
}

interface Product {
  id: string;
  slug: string;
  name: string;
  sku: string;
  product_type: string;
  price_gross: number | null;
  base_price_value: number | null;
  base_price_unit: string | null;
  short_tagline: string | null;
  is_active: boolean;
  image_url: string | null;
  last_embedded_at: string | null;
  product_media: ProductMedia[];
  product_sections: ProductSection[];
  product_attributes: { id: string; key: string; value: string; group_name: string }[];
  product_documents?: { id: string; type: string; title: string }[];
}

const SECTION_TYPE_LABELS: Record<string, string> = {
  bullets: 'Highlights',
  long_description: 'Beschreibung',
  usage: 'Anwendung',
  unique_ingredients: 'Besondere Inhaltsstoffe',
  ingredients_list: 'Inhaltsstoffe',
  warnings: 'Hinweise',
};

export function AdminProductEditor() {
  const { slug } = useParams<{ slug: string }>();
  const [product, setProduct] = useState<Product | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Form state
  const [name, setName] = useState('');
  const [priceGross, setPriceGross] = useState('');
  const [basePriceValue, setBasePriceValue] = useState('');
  const [basePriceUnit, setBasePriceUnit] = useState('');
  const [shortTagline, setShortTagline] = useState('');
  const [isActive, setIsActive] = useState(true);

  // Sections editing
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const [sectionContent, setSectionContent] = useState('');

  // Media drag & drop
  const [draggedMediaId, setDraggedMediaId] = useState<string | null>(null);
  const [dragOverMediaId, setDragOverMediaId] = useState<string | null>(null);

  // Embedding state
  const [isEmbedding, setIsEmbedding] = useState(false);
  const [embedResult, setEmbedResult] = useState<{ success: boolean; count?: number } | null>(null);

  useEffect(() => {
    if (slug) {
      loadProduct();
    }
  }, [slug]);

  const loadProduct = async () => {
    if (!slug) return;
    setIsLoading(true);
    try {
      const data = await getAdminProduct(slug);
      setProduct(data);
      setName(data.name);
      setPriceGross(data.price_gross?.toString() || '');
      setBasePriceValue(data.base_price_value?.toString() || '');
      setBasePriceUnit(data.base_price_unit || '');
      setShortTagline(data.short_tagline || '');
      setIsActive(data.is_active);
    } catch (error) {
      logger.error('Error loading product:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveBasicInfo = async () => {
    if (!product) return;
    setIsSaving(true);
    try {
      await updateProduct(product.id, {
        name,
        price_gross: priceGross ? parseFloat(priceGross) : undefined,
        base_price_value: basePriceValue ? parseFloat(basePriceValue) : undefined,
        base_price_unit: basePriceUnit || undefined,
        short_tagline: shortTagline || undefined,
        is_active: isActive,
      });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      logger.error('Error saving product:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveSection = async (sectionId: string) => {
    try {
      await updateProductSection(sectionId, sectionContent);
      setProduct((prev) =>
        prev
          ? {
              ...prev,
              product_sections: prev.product_sections.map((s) =>
                s.id === sectionId ? { ...s, content: sectionContent } : s
              ),
            }
          : null
      );
      setEditingSection(null);
      setSectionContent('');
    } catch (error) {
      logger.error('Error saving section:', error);
    }
  };

  const handleDeleteMedia = async (mediaId: string) => {
    if (!confirm('Bild wirklich löschen?')) return;
    try {
      await deleteProductMedia(mediaId);
      setProduct((prev) =>
        prev
          ? {
              ...prev,
              product_media: prev.product_media.filter((m) => m.id !== mediaId),
            }
          : null
      );
    } catch (error) {
      logger.error('Error deleting media:', error);
    }
  };

  const handleMediaDragStart = (mediaId: string) => {
    setDraggedMediaId(mediaId);
  };

  const handleMediaDragOver = (e: React.DragEvent, mediaId: string) => {
    e.preventDefault();
    if (draggedMediaId && draggedMediaId !== mediaId) {
      setDragOverMediaId(mediaId);
    }
  };

  const handleMediaDragLeave = () => {
    setDragOverMediaId(null);
  };

  const handleMediaDrop = async (targetMediaId: string) => {
    if (!draggedMediaId || !product || draggedMediaId === targetMediaId) {
      setDraggedMediaId(null);
      setDragOverMediaId(null);
      return;
    }

    // Get sorted media array
    const sortedMedia = [...product.product_media].sort((a, b) => a.sort_order - b.sort_order);

    // Find indices
    const draggedIndex = sortedMedia.findIndex(m => m.id === draggedMediaId);
    const targetIndex = sortedMedia.findIndex(m => m.id === targetMediaId);

    if (draggedIndex === -1 || targetIndex === -1) return;

    // Reorder array
    const [removed] = sortedMedia.splice(draggedIndex, 1);
    sortedMedia.splice(targetIndex, 0, removed);

    // Create new sort_order values
    const reorderedMedia = sortedMedia.map((media, index) => ({
      ...media,
      sort_order: index,
    }));

    // Optimistically update UI
    setProduct((prev) =>
      prev
        ? {
            ...prev,
            product_media: reorderedMedia,
          }
        : null
    );

    // Reset drag state
    setDraggedMediaId(null);
    setDragOverMediaId(null);

    // Save to database
    try {
      await reorderProductMedia(reorderedMedia.map(m => ({ id: m.id, sort_order: m.sort_order })));
    } catch (error) {
      logger.error('Error reordering media:', error);
      // Reload to get correct order on error
      loadProduct();
    }
  };

  const handleMediaDragEnd = () => {
    setDraggedMediaId(null);
    setDragOverMediaId(null);
  };

  const handleEmbed = async () => {
    if (!product) return;
    setIsEmbedding(true);
    setEmbedResult(null);
    try {
      const result = await embedProduct(product.id);
      setEmbedResult({ success: true, count: result.embedded_count });
      // Update local state with new timestamp
      setProduct((prev) =>
        prev ? { ...prev, last_embedded_at: new Date().toISOString() } : null
      );
      setTimeout(() => setEmbedResult(null), 5000);
    } catch (error) {
      logger.error('Error embedding product:', error);
      setEmbedResult({ success: false });
      setTimeout(() => setEmbedResult(null), 5000);
    } finally {
      setIsEmbedding(false);
    }
  };

  const formatEmbeddedDate = (date: string | null) => {
    if (!date) return null;
    return new Date(date).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getProductImage = () => {
    if (product?.image_url) return product.image_url;
    // Sort by sort_order to get the correct cover image
    const sortedMedia = [...(product?.product_media || [])].sort((a, b) => a.sort_order - b.sort_order);
    const firstImage = sortedMedia.find((m) => m.type === 'image');
    return firstImage?.url || null;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="text-center py-20">
        <AlertCircle className="w-12 h-12 text-slate-600 mx-auto mb-4" />
        <p className="text-slate-400">Produkt nicht gefunden</p>
        <Link
          to="/admin/products"
          className="inline-flex items-center gap-2 mt-4 text-orange-400 hover:text-orange-300"
        >
          <ArrowLeft className="w-4 h-4" />
          Zurück zur Übersicht
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link
            to="/admin/products"
            className="p-2 bg-slate-800 text-slate-400 rounded-xl hover:bg-slate-700 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex items-center gap-4">
            {getProductImage() ? (
              <img
                src={getProductImage()!}
                alt=""
                className="w-14 h-14 rounded-xl object-cover"
              />
            ) : (
              <div className="w-14 h-14 bg-slate-700 rounded-xl flex items-center justify-center">
                <Package className="w-7 h-7 text-slate-500" />
              </div>
            )}
            <div>
              <h1 className="text-2xl font-bold text-white">{product.name}</h1>
              <p className="text-slate-400 font-mono">{product.sku}</p>
            </div>
          </div>
        </div>

        <button
          onClick={handleSaveBasicInfo}
          disabled={isSaving}
          className="flex items-center gap-2 px-6 py-2.5 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition-colors disabled:opacity-50"
        >
          {saveSuccess ? (
            <>
              <Check className="w-4 h-4" />
              Gespeichert
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              {isSaving ? 'Speichert...' : 'Speichern'}
            </>
          )}
        </button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left Column - Basic Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Info */}
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Grundinformationen</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-slate-400 mb-2">Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-2">Kurzbeschreibung</label>
                <input
                  type="text"
                  value={shortTagline}
                  onChange={(e) => setShortTagline(e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Preis (Brutto)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={priceGross}
                    onChange={(e) => setPriceGross(e.target.value)}
                    className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Grundpreis</label>
                  <input
                    type="number"
                    step="0.01"
                    value={basePriceValue}
                    onChange={(e) => setBasePriceValue(e.target.value)}
                    className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Einheit</label>
                  <input
                    type="text"
                    value={basePriceUnit}
                    onChange={(e) => setBasePriceUnit(e.target.value)}
                    placeholder="z.B. 100ml"
                    className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setIsActive(!isActive)}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    isActive ? 'bg-emerald-500' : 'bg-slate-700'
                  }`}
                >
                  <span
                    className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                      isActive ? 'left-7' : 'left-1'
                    }`}
                  />
                </button>
                <span className="text-slate-300">
                  {isActive ? 'Produkt aktiv' : 'Produkt inaktiv'}
                </span>
              </div>
            </div>
          </div>

          {/* Sections */}
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Inhalte</h2>
            <div className="space-y-4">
              {product.product_sections
                .sort((a, b) => a.sort_order - b.sort_order)
                .map((section) => (
                  <div key={section.id} className="bg-slate-800 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-medium text-white">
                        {SECTION_TYPE_LABELS[section.section_type] || section.title}
                      </h3>
                      {editingSection !== section.id && (
                        <button
                          onClick={() => {
                            setEditingSection(section.id);
                            setSectionContent(section.content);
                          }}
                          className="text-sm text-orange-400 hover:text-orange-300"
                        >
                          Bearbeiten
                        </button>
                      )}
                    </div>
                    {editingSection === section.id ? (
                      <div className="space-y-3">
                        <textarea
                          value={sectionContent}
                          onChange={(e) => setSectionContent(e.target.value)}
                          rows={6}
                          className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-xl text-white placeholder-slate-500 resize-none focus:outline-none focus:ring-2 focus:ring-orange-500 font-mono text-sm"
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleSaveSection(section.id)}
                            className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-sm"
                          >
                            <Save className="w-4 h-4" />
                            Speichern
                          </button>
                          <button
                            onClick={() => {
                              setEditingSection(null);
                              setSectionContent('');
                            }}
                            className="flex items-center gap-2 px-4 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 transition-colors text-sm"
                          >
                            <X className="w-4 h-4" />
                            Abbrechen
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-slate-400 whitespace-pre-wrap line-clamp-4">
                        {section.content || 'Kein Inhalt'}
                      </div>
                    )}
                  </div>
                ))}
            </div>
          </div>
        </div>

        {/* Right Column - Media & Attributes */}
        <div className="space-y-6">
          {/* Media */}
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Bilder</h2>
              {product.product_media.length > 1 && (
                <span className="text-xs text-slate-500 flex items-center gap-1">
                  <GripVertical className="w-3 h-3" />
                  Ziehen zum Sortieren
                </span>
              )}
            </div>
            {product.product_media.length > 0 ? (
              <div className="grid grid-cols-2 gap-3">
                {product.product_media
                  .sort((a, b) => a.sort_order - b.sort_order)
                  .map((media, index) => (
                    <div
                      key={media.id}
                      draggable
                      onDragStart={() => handleMediaDragStart(media.id)}
                      onDragOver={(e) => handleMediaDragOver(e, media.id)}
                      onDragLeave={handleMediaDragLeave}
                      onDrop={() => handleMediaDrop(media.id)}
                      onDragEnd={handleMediaDragEnd}
                      className={`relative group cursor-grab active:cursor-grabbing transition-all ${
                        draggedMediaId === media.id ? 'opacity-50 scale-95' : ''
                      } ${
                        dragOverMediaId === media.id
                          ? 'ring-2 ring-orange-500 ring-offset-2 ring-offset-slate-900'
                          : ''
                      }`}
                    >
                      <img
                        src={media.url}
                        alt={media.alt || ''}
                        className="w-full aspect-square object-cover rounded-xl pointer-events-none"
                      />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center gap-2">
                        <div className="p-2 bg-slate-800 text-white rounded-lg">
                          <GripVertical className="w-4 h-4" />
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteMedia(media.id);
                          }}
                          className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <span className="absolute top-2 left-2 px-2 py-0.5 bg-slate-900/80 text-slate-300 text-xs rounded">
                        {media.type}
                      </span>
                      {index === 0 && (
                        <span className="absolute top-2 right-2 px-2 py-0.5 bg-orange-500 text-white text-xs rounded font-medium">
                          Hauptbild
                        </span>
                      )}
                    </div>
                  ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <ImageIcon className="w-10 h-10 text-slate-600 mx-auto mb-2" />
                <p className="text-slate-400 text-sm">Keine Bilder vorhanden</p>
              </div>
            )}
          </div>

          {/* Attributes */}
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Attribute</h2>
            {product.product_attributes.length > 0 ? (
              <div className="space-y-2">
                {product.product_attributes.map((attr) => (
                  <div
                    key={attr.id}
                    className="flex justify-between py-2 border-b border-slate-800 last:border-0"
                  >
                    <span className="text-slate-400 text-sm">{attr.key}</span>
                    <span className="text-slate-200 text-sm">{attr.value}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-400 text-sm text-center py-4">Keine Attribute</p>
            )}
          </div>

          {/* Product Info */}
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Produktdetails</h2>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-400">SKU</span>
                <span className="text-slate-200 font-mono">{product.sku}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Typ</span>
                <span className="text-slate-200">{product.product_type}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Slug</span>
                <span className="text-slate-200 font-mono text-xs">{product.slug}</span>
              </div>
            </div>
          </div>

          {/* AI Knowledge Base */}
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <Brain className="w-5 h-5 text-violet-400" />
              <h2 className="text-lg font-semibold text-white">KI-Wissensdatenbank</h2>
            </div>

            <div className="space-y-4">
              {/* Embedding Status */}
              <div className="flex items-center justify-between py-3 px-4 bg-slate-800 rounded-xl">
                <div className="flex items-center gap-3">
                  {product.last_embedded_at ? (
                    <>
                      <div className="w-2 h-2 bg-emerald-400 rounded-full" />
                      <div>
                        <p className="text-sm text-slate-200">Embedded</p>
                        <p className="text-xs text-slate-500 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatEmbeddedDate(product.last_embedded_at)}
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="w-2 h-2 bg-slate-500 rounded-full" />
                      <div>
                        <p className="text-sm text-slate-400">Nicht embedded</p>
                        <p className="text-xs text-slate-500">Für KI-Suche nicht verfügbar</p>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Content Info */}
              <div className="text-xs text-slate-500 space-y-1">
                <p>Folgende Inhalte werden embedded:</p>
                <ul className="list-disc list-inside space-y-0.5 text-slate-600">
                  <li>Grundinfos (Name, Preis, Kategorie)</li>
                  <li>{product.product_sections.length} Inhalts-Sektionen</li>
                  <li>{product.product_attributes.length} Attribute</li>
                  {product.product_documents && product.product_documents.length > 0 && (
                    <li>{product.product_documents.length} Dokumente</li>
                  )}
                </ul>
              </div>

              {/* Result Message */}
              {embedResult && (
                <div
                  className={`flex items-center gap-2 py-2 px-3 rounded-lg text-sm ${
                    embedResult.success
                      ? 'bg-emerald-500/10 text-emerald-400'
                      : 'bg-red-500/10 text-red-400'
                  }`}
                >
                  {embedResult.success ? (
                    <>
                      <Check className="w-4 h-4" />
                      {embedResult.count} Inhalte embedded
                    </>
                  ) : (
                    <>
                      <AlertCircle className="w-4 h-4" />
                      Fehler beim Embedding
                    </>
                  )}
                </div>
              )}

              {/* Embed Button */}
              <button
                onClick={handleEmbed}
                disabled={isEmbedding}
                className="w-full flex items-center justify-center gap-2 py-3 bg-violet-600 text-white rounded-xl hover:bg-violet-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isEmbedding ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Embedding läuft...
                  </>
                ) : product.last_embedded_at ? (
                  <>
                    <RefreshCw className="w-4 h-4" />
                    Neu embedden
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    Jetzt embedden
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
